<template>
  <div>
    <section>
      <div class="px-6">
        <h3 class="mb-4">{{$t('hr_insights')}}</h3>
      </div>
    </section>

    <!-- Key Metrics -->
    <section class="px-6 py-4">
      <div class="row g-2">
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-people-fill" class="bi bi-people-fill text-primary" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{metrics.totalEmployees || 0}}</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('total_employees')}}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-person-check-fill" class="bi bi-person-check-fill text-success" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{metrics.presentToday || 0}}</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('present_today')}}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-calendar-x-fill" class="bi bi-calendar-x-fill text-danger" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{metrics.absentToday || 0}}</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('absent_today')}}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-clock-fill" class="bi bi-clock-fill text-warning" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{metrics.lateToday || 0}}</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('late_today')}}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-calendar-event-fill" class="bi bi-calendar-event-fill text-info" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{metrics.onLeaveToday || 0}}</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('on_leave_today')}}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center">
            <div class="card-body py-2 px-2">
              <AppIcon name="bi-graph-up-arrow" class="bi bi-graph-up-arrow text-primary" style="font-size: 1.5rem" />
              <h5 class="mt-1 mb-1">{{attendanceRate}}%</h5>
              <small class="text-muted" style="font-size: 0.75rem">{{$t('attendance_rate')}}</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="px-6 py-4">
      <div class="row">
        <!-- Left Column -->
        <div class="col-md-8">
          <!-- Holiday Calendar -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{$t('holiday_calendar')}}</h5>
              <div>
                <button @click="previousMonth" class="btn btn-sm btn-outline-secondary me-1">
                  <AppIcon name="bi-chevron-left" class="bi bi-chevron-left" />
                </button>
                <span class="mx-2">{{currentMonthYear}}</span>
                <button @click="nextMonth" class="btn btn-sm btn-outline-secondary">
                  <AppIcon name="bi-chevron-right" class="bi bi-chevron-right" />
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="calendar">
                <div class="calendar-header">
                  <div v-for="day in weekDays" :key="day" class="calendar-day-header">
                    {{$t(day)}}
                  </div>
                </div>
                <div class="calendar-body">
                  <div 
                    v-for="(day, index) in calendarDays" 
                    :key="index"
                    class="calendar-day"
                    :class="{
                      'other-month': !day.currentMonth,
                      'today': day.isToday,
                      'has-leave': day.leaves && day.leaves.length > 0
                    }"
                    @click="showDayDetails(day)"
                  >
                    <div class="day-number">{{day.date}}</div>
                    <div v-if="day.leaves && day.leaves.length > 0" class="leave-indicators">
                      <div 
                        v-for="leave in day.leaves.slice(0, 2)" 
                        :key="leave.id"
                        class="leave-badge"
                        :title="leave.employee.name"
                      >
                        {{leave.employee.name.substring(0, 2)}}
                      </div>
                      <div v-if="day.leaves.length > 2" class="leave-badge more">
                        +{{day.leaves.length - 2}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Attendance Trends (7 days) -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">{{$t('trends_last_7_days')}}</h5>
            </div>
            <div class="card-body">
              <div style="overflow-x:auto">
                <table class="table table-sm table-borderless text-center small">
                  <thead>
                    <tr class="text-secondary">
                      <th>{{$t('date')}}</th>
                      <th class="text-success">{{$t('present')}}</th>
                      <th class="text-danger">{{$t('absent')}}</th>
                      <th class="text-warning">{{$t('late')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="trend in attendanceTrends" :key="trend.date">
                      <td>{{ formatShortDate(trend.date) }}</td>
                      <td><span class="badge bg-success">{{ trend.present }}</span></td>
                      <td><span class="badge bg-danger">{{ trend.absent }}</span></td>
                      <td><span class="badge bg-warning">{{ trend.late }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="attendanceTrends.length === 0" class="text-center py-4 bg-light rounded" style="border: 1px dashed #ccc;">
                <AppIcon name="bi-graph-up" class="bi bi-graph-up text-muted" style="font-size: 2rem;" />
                <p class="mt-2 mb-0 text-muted">{{$t('no_attendance_records')}}</p>
              </div>
            </div>
          </div>

          <!-- Late Arrivals Today -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">{{$t('late_arrivals_today')}}</h5>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>{{$t('employee')}}</th>
                      <th>{{$t('expected_time')}}</th>
                      <th>{{$t('actual_time')}}</th>
                      <th>{{$t('delay')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="late in lateArrivals" :key="late.id">
                      <td>{{late.employee.name}}</td>
                      <td>{{ getExpectedTime(late.employee) }}</td>
                      <td>{{formatTime(late.check_in)}}</td>
                      <td class="text-danger">{{calculateDelay(late.check_in, late.employee)}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="lateArrivals.length === 0" class="text-center py-4 bg-light rounded" style="border: 1px dashed #ccc;">
                <AppIcon name="bi-clock" class="bi bi-clock text-muted" style="font-size: 2rem;" />
                <p class="mt-2 mb-0 text-muted">{{$t('no_late_arrivals')}}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="col-md-4">
          <!-- Pending Requests -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">{{$t('pending_requests')}}</h5>
            </div>
            <div class="card-body">
              <div v-if="pendingRequests.leaves > 0" class="d-flex justify-content-between align-items-center mb-2">
                <span>{{$t('leave_requests')}}</span>
                <span class="badge bg-warning">{{pendingRequests.leaves}}</span>
              </div>
              <div v-else class="text-center py-4 bg-light rounded" style="border: 1px dashed #ccc;">
                <AppIcon name="bi-inbox" class="bi bi-inbox text-muted" style="font-size: 2rem;" />
                <p class="mt-2 mb-0 text-muted">{{$t('no_pending_requests')}}</p>
              </div>
            </div>
          </div>

          <!-- On Leave Today -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">{{$t('on_leave_today')}}</h5>
            </div>
            <div class="card-body">
              <div v-for="leave in todayLeaves" :key="leave.id" class="mb-2">
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1">
                    <strong>{{leave.employee.name}}</strong>
                    <br>
                    <small class="text-muted">{{$t(leave.leave_type)}}</small>
                  </div>
                  <span class="badge bg-info">{{leave.days_count}} {{$t('days')}}</span>
                </div>
              </div>
              <div v-if="todayLeaves.length === 0" class="text-center py-4 bg-light rounded" style="border: 1px dashed #ccc;">
                <AppIcon name="bi-calendar-event" class="bi bi-calendar-event text-muted" style="font-size: 2rem;" />
                <p class="mt-2 mb-0 text-muted">{{$t('no_one_on_leave')}}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Day Details Modal -->
    <div class="modal fade" id="dayDetailsModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{selectedDay.fullDate}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <h6>{{$t('employees_on_leave')}}:</h6>
            <div v-for="leave in selectedDay.leaves" :key="leave.id" class="mb-2">
              <div class="d-flex justify-content-between">
                <span><strong>{{leave.employee.name}}</strong></span>
                <span class="badge bg-info">{{$t(leave.leave_type)}}</span>
              </div>
              <small class="text-muted">
                {{formatDate(leave.start_date)}} - {{formatDate(leave.end_date)}}
              </small>
            </div>
            <div v-if="!selectedDay.leaves || selectedDay.leaves.length === 0" class="text-center py-4 bg-light rounded" style="border: 1px dashed #ccc;">
              <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted" style="font-size: 2rem;" />
              <p class="mt-2 mb-0 text-muted">{{$t('no_leaves_on_this_day')}}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'

export default {
  name: 'HrInsights',
  data() {
    return {
      helper,
      metrics: {
        totalEmployees: 0,
        presentToday: 0,
        absentToday: 0,
        lateToday: 0,
        onLeaveToday: 0
      },
      currentDate: new Date(),
      calendarDays: [],
      weekDays: ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
      lateArrivals: [],
      todayLeaves: [],
      departmentStats: [],
      attendanceTrends: [],
      pendingRequests: {
        leaves: 0
      },
      selectedDay: {
        fullDate: '',
        leaves: []
      },
      dayDetailsModal: null
    }
  },
  computed: {
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    },
    attendanceRate() {
      if (!this.metrics.totalEmployees) return 0
      const rate = (this.metrics.presentToday / this.metrics.totalEmployees) * 100
      return Math.round(rate)
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_hr_insights') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_hr_insights') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchMetrics()
    this.fetchLateArrivals()
    this.fetchTodayLeaves()
    this.fetchDepartmentStats()
    this.fetchPendingRequests()
    this.fetchAttendanceTrends()
    this.generateCalendar()
    // eslint-disable-next-line no-undef
    this.dayDetailsModal = new bootstrap.Modal(document.getElementById('dayDetailsModal'))
  },
  methods: {
    formatDate(date) {
      return helper.formatDate(date)
    },
    formatShortDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    formatTime(datetime) {
      if (!datetime) return '-'
      const date = new Date(datetime)
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    },
    getExpectedTime(employee) {
      // Try to parse a time from the employee's work_schedule (e.g. "Mon-Fri 9:00-17:00")
      if (employee && employee.work_schedule) {
        const match = employee.work_schedule.match(/(\d{1,2}:\d{2})/);
        if (match) return match[1]
      }
      return '09:00'
    },
    calculateDelay(checkIn, employee) {
      const time = new Date(checkIn)
      const expectedTimeStr = this.getExpectedTime(employee)
      const [expHour, expMin] = expectedTimeStr.split(':').map(Number)
      
      const expected = new Date(checkIn)
      expected.setHours(expHour, expMin, 0, 0)
      
      const diff = time - expected
      const minutes = Math.floor(diff / 60000)
      
      if (minutes <= 0) return '0 min'
      
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      
      return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`
    },
    async fetchMetrics() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/hr/insights/metrics`)
        this.metrics = response.data
      } catch (error) {
        alert(error)
      }
    },
    async fetchLateArrivals() {
      try {
        const today = new Date().toISOString().split('T')[0]
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employee-attendances?date=${today}&status=late`)
        this.lateArrivals = response.data.data || []
      } catch (error) {
        alert(error)
      }
    },
    async fetchTodayLeaves() {
      try {
        const today = new Date().toISOString().split('T')[0]
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?date=${today}&status=approved`)
        this.todayLeaves = response.data.data || []
      } catch (error) {
        alert(error)
      }
    },
    async fetchDepartmentStats() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/hr/insights/departments`)
        this.departmentStats = response.data || []
      } catch (error) {
        alert(error)
      }
    },
    async fetchAttendanceTrends() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/hr/insights/attendance-trends?days=7`)
        this.attendanceTrends = response.data || []
      } catch (error) {
        alert(error)
      }
    },
    async fetchPendingRequests() {
      try {
        const leavesResponse = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?status=pending&per_page=1`)
        this.pendingRequests.leaves = leavesResponse.data.count || 0
      } catch (error) {
        alert(error)
      }
    },
    async generateCalendar() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)
      const prevLastDay = new Date(year, month, 0)
      
      const firstDayOfWeek = firstDay.getDay()
      const lastDateOfMonth = lastDay.getDate()
      const prevLastDate = prevLastDay.getDate()
      
      let days = []
      
      // Previous month days
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: prevLastDate - i,
          currentMonth: false,
          fullDate: new Date(year, month - 1, prevLastDate - i),
          leaves: []
        })
      }
      
      // Current month days
      for (let i = 1; i <= lastDateOfMonth; i++) {
        const dayDate = new Date(year, month, i)
        days.push({
          date: i,
          currentMonth: true,
          fullDate: dayDate,
          isToday: this.isToday(dayDate),
          leaves: []
        })
      }
      
      // Next month days
      const remainingDays = 42 - days.length
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: i,
          currentMonth: false,
          fullDate: new Date(year, month + 1, i),
          leaves: []
        })
      }
      
      this.calendarDays = days
      await this.fetchMonthLeaves()
    },
    async fetchMonthLeaves() {
      try {
        const year = this.currentDate.getFullYear()
        const month = this.currentDate.getMonth()
        const start = new Date(year, month, 1).toISOString().split('T')[0]
        const end = new Date(year, month + 1, 0).toISOString().split('T')[0]
        
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?start_date=${start}&end_date=${end}&status=approved`)
        const leaves = response.data.data || []
        
        // Map leaves to calendar days
        this.calendarDays.forEach(day => {
          day.leaves = leaves.filter(leave => {
            const leaveStart = new Date(leave.start_date)
            const leaveEnd = new Date(leave.end_date)
            const dayDate = day.fullDate
            
            return dayDate >= leaveStart && dayDate <= leaveEnd
          })
        })
      } catch (error) {
        alert(error)
      }
    },
    isToday(date) {
      const today = new Date()
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear()
    },
    previousMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1)
      this.generateCalendar()
    },
    nextMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1)
      this.generateCalendar()
    },
    showDayDetails(day) {
      if (day.leaves && day.leaves.length > 0) {
        this.selectedDay = {
          fullDate: day.fullDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          leaves: day.leaves
        }
        this.dayDetailsModal.show()
      }
    }
  }
}
</script>

<style scoped>
.calendar {
  width: 100%;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  margin-bottom: 5px;
}

.calendar-day-header {
  text-align: center;
  font-weight: 600;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
}

.calendar-day {
  min-height: 80px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.calendar-day:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.calendar-day.other-month {
  opacity: 0.3;
}

.calendar-day.today {
  background-color: #e3f2fd;
  border-color: #2196f3;
  font-weight: 600;
}

.calendar-day.has-leave {
  background-color: #fff3e0;
  border-color: #ff9800;
}

.day-number {
  font-size: 14px;
  margin-bottom: 4px;
}

.leave-indicators {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.leave-badge {
  font-size: 10px;
  padding: 2px 4px;
  background-color: #2196f3;
  color: white;
  border-radius: 3px;
  white-space: nowrap;
}

.leave-badge.more {
  background-color: #666;
}

.card {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.card-header {
  background-color: #f8f9fa;
  font-weight: 600;
}
</style>
